// Émission tir-et-oublie des événements d'exposition du parcours autodiag
// (ticket FIX-429) vers le service LLM : POST {AUTODIAG_BASE}/api/exposure.
//
// Contrat (source de vérité : contract.md du ticket) :
//   - Headers : Content-Type: application/json + X-Autodiag-Token (token
//     AUTODIAG_EXPOSURE_TOKEN, inliné au build par DefinePlugin — LISIBLE dans
//     le bundle navigateur : filtre anti-bruit, PAS une authentification).
//   - Body : { booking_uuid, event, value?, device, origin: 'front', ts }.
//     `value` ('llm' | 'tally') requis si et seulement si event = 'flow_chosen'.
//   - fetch + keepalive: true (survit à la navigation qui suit) ;
//     navigator.sendBeacon est INTERDIT (aucun header possible).
//   - Tir-et-oublie strict : aucune réponse lue, aucune exception ne remonte
//     jamais à l'appelant.
//
// Garde anti-double-envoi : une seule émission par (event, booking_uuid) —
// Set module-level (remontage React, restauration bfcache) + sessionStorage
// (rechargement complet du document dans le même onglet). Le serveur déduplique
// aussi sur la même clé (premier arrivé gagne) : la garde client est un
// complément, jamais une garantie.
import { getAutodiagBase } from './autodiag';
import { size } from '../styles/constants';

export const EXPOSURE_EVENTS = {
  CHOICE_VIEWED: 'choice_viewed',
  FLOW_CHOSEN: 'flow_chosen',
};

export const FLOW_VALUES = {
  LLM: 'llm',
  TALLY: 'tally',
};

const TOKEN = process.env.AUTODIAG_EXPOSURE_TOKEN || '';
const SESSION_STORAGE_KEY = 'rj_autodiag_exposure_sent';

// Token vide dans un build de prod : tous les events partiraient avec un
// header vide et seraient rejetés en 401, en silence (.catch masque tout).
// Warn unique au chargement du module — pas de throw, pas d'échec de build :
// les previews sans token doivent continuer de builder.
if (
  typeof window !== 'undefined' &&
  process.env.NODE_ENV === 'production' &&
  !TOKEN
) {
  // eslint-disable-next-line no-console
  console.warn(
    '[autodiag] AUTODIAG_EXPOSURE_TOKEN absent du build : les événements ' +
      "d'exposition seront rejetés (401) par le service.",
  );
}

// Clés déjà émises pendant la vie du module (survit au démontage/remontage de
// la scène — la CSSTransition unmountOnExit de Repair.jsx démonte réellement).
const sentKeys = new Set();

function isValidEvent(event) {
  return (
    event === EXPOSURE_EVENTS.CHOICE_VIEWED ||
    event === EXPOSURE_EVENTS.FLOW_CHOSEN
  );
}

function isValidValue(value) {
  return value === FLOW_VALUES.LLM || value === FLOW_VALUES.TALLY;
}

// Seuil 768 px, même motif que getVersion() (src/utils/utils.jsx).
function getDevice() {
  return window.innerWidth < Number(size.sm.replace('px', ''))
    ? 'mobile'
    : 'desktop';
}

// Tout accès sessionStorage sous try/catch : indisponible en navigation privée
// Safari ou si les cookies sont bloqués — la garde mémoire suffit alors.
function readSessionSentKeys() {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    const parsed = JSON.parse(raw || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

function markSessionSentKey(key) {
  try {
    const sent = readSessionSentKeys();
    if (!sent.includes(key)) {
      sent.push(key);
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sent));
    }
  } catch (e) {
    // no-op — voir readSessionSentKeys.
  }
}

/**
 * Émet un événement d'exposition autodiag. Synchrone du point de vue de
 * l'appelant : ne retourne rien, ne lève jamais, ne bloque jamais l'UI.
 * @param {object} params
 * @param {string} params.bookingUuid UUID v4 de la réservation (clé de jointure)
 * @param {string} params.event EXPOSURE_EVENTS.CHOICE_VIEWED | FLOW_CHOSEN
 * @param {string} [params.value] FLOW_VALUES.LLM | TALLY — requis ssi FLOW_CHOSEN
 */
export function sendAutodiagExposure({ bookingUuid, event, value } = {}) {
  if (typeof window === 'undefined') return; // SSR : jamais d'émission serveur
  if (typeof fetch !== 'function') return;
  if (!bookingUuid) return; // pas d'event sans clé de jointure
  if (!isValidEvent(event)) return; // allowlist client, miroir du serveur
  if (event === EXPOSURE_EVENTS.FLOW_CHOSEN && !isValidValue(value)) return;

  const dedupKey = `${event}:${bookingUuid}`;
  if (sentKeys.has(dedupKey)) return;
  if (readSessionSentKeys().includes(dedupKey)) {
    sentKeys.add(dedupKey);
    return;
  }
  sentKeys.add(dedupKey);
  markSessionSentKey(dedupKey);

  const body = {
    booking_uuid: bookingUuid,
    event,
    device: getDevice(),
    origin: 'front',
    ts: Date.now(),
  };
  if (event === EXPOSURE_EVENTS.FLOW_CHOSEN) {
    body.value = value;
  }

  try {
    fetch(`${getAutodiagBase()}/api/exposure`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        'X-Autodiag-Token': TOKEN,
      },
      body: JSON.stringify(body),
    }).catch(() => {});
  } catch (e) {
    // Jamais bloquant pour l'utilisateur, quoi qu'il arrive.
  }
}
